import { FC, Dispatch, SetStateAction, useEffect, useState } from 'react';
import { Field, FieldProps, Formik } from 'formik';
import { AlertComponent, CSVLink, FieldRow, Flatfile, Flex, Modal } from '.';
import { format as formatDate } from 'date-fns';
import { client } from '../../apollo';
import {
  GET_PROGRAMME_MONITORINGFORMDATA,
  UPSERT_MONITORINGFORMSUBMISSION,
  GET_PROGRAMME_MONITORINGFORM,
  ALL_MYFORMS,
  GET_MONITORING_FORM_SUBMISSION,
} from '../../graphql';
import { flatfileMonitoringFormValidator, getPortal, parseErrorMessage } from '../../utils';
import { FieldType as FlatFileFieldType } from '../../consts/flatfileFields';
import { Option } from '../../utils/types';
import { TemplateType } from '../../consts/uploadTemplates';
import { getOrganisationName } from '../../utils/format';

interface Props {
  setShowUploadModal: (value: boolean) => void;
  monitoringFormDataForUpload: any;
  setMonitoringFormDataForUpload: Dispatch<SetStateAction<any>>;
  showUploadModal: boolean;
  tableData: any;
}

type FormType = {
  ministryId: Option | undefined;
  typeId: Option | undefined;
  uploadData: any;
  fields: FlatFileFieldType[];
};

const INITIAL_VALUES: FormType = {
  ministryId: undefined,
  typeId: undefined,
  uploadData: undefined,
  fields: [],
};

const getUpsertMonitoringFormSubmissionInput = (
  formData: any,
  values: any,
  existingData: any,
  tableData: any[] = []
): any => {
  const reporters =
    formData.formReporters.length > 0
      ? formData.formReporters
      : [
          {
            id: '',
            organisation: {
              id: '',
              funder: null,
              serviceProvider: null,
              institution: null,
            },
          },
        ];

  const input: { submissions: any[] } = {
    submissions: [],
  };

  const statusDate = new Date();

  // Group all rows by Period Date.
  // Rows without Period Date fall back to the default due date period (legacy behavior).
  const periodDateGroups = values.reduce((acc: any, row: any) => {
    const periodKey = row.periodDate;
    if (periodKey !== undefined && periodKey !== null && periodKey !== '') {
      if (!acc[periodKey]) acc[periodKey] = [];
      acc[periodKey].push(row);
    } else {
      const fallbackKey = '__DEFAULT_DUE_DATE_PERIOD__';
      if (!acc[fallbackKey]) acc[fallbackKey] = [];
      acc[fallbackKey].push(row);
    }
    return acc;
  }, {});

  const getPeriodNumberFromGroupKey = (groupKey: string): number | undefined => {
    if (!Array.isArray(tableData) || tableData.length === 0) return undefined;
    const normalizedGroupKey = groupKey
      ? String(groupKey)
          .trim()
          .toLowerCase()
      : '';
    if (!normalizedGroupKey) return undefined;

    const normalizeDateLikeInput = (value: string): string => {
      return value
        .trim()
        .replace(/\s+/g, '')
        .replace(/\//g, '-');
    };

    const parseDateParts = (value: string): { year: number; month: number; day: number } | undefined => {
      const cleaned = normalizeDateLikeInput(value);
      if (!cleaned.includes('-')) return undefined;
      const parts = cleaned.split('-');
      if (parts.length !== 3) return undefined;
      if (!parts.every(p => /^\d+$/.test(p))) return undefined;

      let year = 0;
      let month = 0;
      let day = 0;

      // yyyy-mm-dd
      if (parts[0].length === 4) {
        year = Number(parts[0]);
        month = Number(parts[1]);
        day = Number(parts[2]);
      } else {
        // dd-mm-yyyy (or dd-mm-yy)
        day = Number(parts[0]);
        month = Number(parts[1]);
        year = Number(parts[2].length === 2 ? `20${parts[2]}` : parts[2]);
      }

      if (month < 1 || month > 12 || day < 1 || day > 31 || year < 1900) return undefined;
      return { year, month, day };
    };

    const inputDateParts = parseDateParts(normalizedGroupKey);

    const matchedRow = tableData.find((row: any) => {
      if (!row) return false;
      if (row.periodNumber !== undefined && String(row.periodNumber) === normalizedGroupKey) return true;

      const periodDates = Array.isArray(row.periodDates) ? row.periodDates : [];
      return periodDates.some((dateValue: any) => {
        const d = new Date(dateValue);
        if (isNaN(d.getTime())) return false;

        if (inputDateParts) {
          return (
            d.getFullYear() === inputDateParts.year &&
            d.getMonth() + 1 === inputDateParts.month &&
            d.getDate() === inputDateParts.day
          );
        }

        const candidates = [
          d.toISOString().split('T')[0],
          formatDate(d, 'd MMM yyyy').toLowerCase(),
          formatDate(d, 'dd-MM-yyyy').toLowerCase(),
          formatDate(d, 'yyyy-MM-dd').toLowerCase(),
        ];
        return candidates.includes(normalizedGroupKey);
      });
    });

    return matchedRow?.periodNumber;
  };

  const groups: any[] =
    Object.keys(periodDateGroups).length > 0
      ? Object.entries(periodDateGroups)
      : [[`${formData?.dueDates?.[0]?.periodNumber || 1}`, values]];

  reporters.forEach((reporter: any) => {
    const rowsGroupedByPeriodNumber: { [periodNumber: string]: any[] } = {};

    groups.forEach((group: any) => {
      const periodGroupKey = group?.[0];
      const groupRows = group?.[1];
      const rowsForPeriod = Array.isArray(groupRows) ? groupRows : [];
      const isDefaultDueDateBucket = periodGroupKey === '__DEFAULT_DUE_DATE_PERIOD__';
      const periodNumberFromTable = isDefaultDueDateBucket ? undefined : getPeriodNumberFromGroupKey(periodGroupKey);
      const periodNumber =
        periodNumberFromTable !== undefined && periodNumberFromTable !== null
          ? periodNumberFromTable
          : formData?.dueDates?.[0]?.periodNumber || 1;

      const periodKey = String(periodNumber);
      if (!rowsGroupedByPeriodNumber[periodKey]) rowsGroupedByPeriodNumber[periodKey] = [];
      rowsGroupedByPeriodNumber[periodKey].push(...rowsForPeriod);
    });

    Object.entries(rowsGroupedByPeriodNumber).forEach(([periodKey, rowsForPeriod]) => {
      const periodNumber = Number(periodKey);
      const submission =
        reporter.id === ''
          ? formData.reporterSubmissions.find((rs: any) => rs.periodNumber === periodNumber)
          : formData.reporterSubmissions.find(
              (rs: any) =>
                rs.reporter && rs.reporter.id === reporter.organisation.id && rs.periodNumber === periodNumber
            );

      input.submissions.push({
        id: submission ? submission.id : '',
        monitoringFormId: formData.id,
        periodNumber: periodNumber,
        status: submission
          ? submission.status
          : formData.reporterSubmissions[0]
          ? formData.reporterSubmissions[0].status
          : 'DRAFT',
        statusDate: statusDate,
        reporterId: reporter.organisation.id,
        data: (rowsForPeriod as any[])
          .map((r: any) => {
            let siteId = null;
            if (r.site) {
              const site = formData.indicators
                .flatMap((indicator: any) => (indicator.site ? [indicator.site] : []))
                .find((site: any) => site.name === r.site);
              siteId = site ? site.id : null;
            }

            return {
              indicatorId: formData.indicators.find((a: any) => a.indicator.name === r.indicator)
                ? formData.indicators.find((a: any) => a.indicator.name === r.indicator).indicator.id
                : undefined,
              indicatorName: r.indicator,
              dimensions: Object.entries(r)
                .filter(([key, value]) => key.includes('Dimension'))
                .map(([key, value]) => value)
                .filter((a: any) => a !== '' && a !== null && a !== undefined),
              value:
                r.period === null || r.period === undefined || r.period === ''
                  ? null
                  : isNaN(parseFloat(String(r.period).replace(/[,\s]/g, '')))
                  ? null
                  : parseFloat(String(r.period).replace(/[,\s]/g, '')),
              siteId: siteId,
              annotation: r.annotation,
              progressStatus: r.progressStatus,
            };
          })
          .filter((v: any) => v.value !== null),
        // urls: [{ id: '', name: '', link: '' }],
      });
    });
  });

  return input;
};

const UploadMonitoringForm: FC<Props> = props => {
  const [template, setTemplate] = useState<TemplateType | undefined>(undefined);
  const [fields, setFields] = useState<any[]>([]);
  const [existingData, setExistingData] = useState<any[]>([]);
  const [portal] = useState(getPortal());

  useEffect(() => {
    if (props.monitoringFormDataForUpload) {
      fetchExistingData(props.monitoringFormDataForUpload);
    }
  }, [JSON.stringify(props.monitoringFormDataForUpload)]);

  const fetchExistingData = async (formData: any) => {
    if (formData && formData.reporterSubmissions && formData.reporterSubmissions.length !== 0) {
      const response: any[] = await client
        .query({
          query: GET_PROGRAMME_MONITORINGFORMDATA,
          variables: {
            input: {
              submissionIds: formData.reporterSubmissions && formData.reporterSubmissions.map((r: any) => r.id),
            },
          },
        })
        .then(({ data }: any) => {
          var result: any[] = [];
          if (data && data.monitoringFormData) {
            result = data.monitoringFormData.map((value: any) => {
              const d: any = {
                id: value.id,
                indicatorId: value.indicator.id,
                indicatorName: value.indicator.name,
                periodNumber: value.periodNumber,
                dimensionReporter: value.dimensionReporter
                  ? {
                      id: value.dimensionReporter.id,
                      name: getOrganisationName(value.dimensionReporter),
                    }
                  : null,
                dimensions: [],
                value: value.value,
                site: value.site,
                annotation: value.annotation,
                progressStatus: value.progressStatus,
              };
              if (value.dimension1) {
                d.dimensions.push(value.dimension1);
                if (value.dimension2) {
                  d.dimensions.push(value.dimension2);
                  if (value.dimension3) {
                    d.dimensions.push(value.dimension3);
                    if (value.dimension4) {
                      d.dimensions.push(value.dimension4);
                      if (value.dimension5) {
                        d.dimensions.push(value.dimension5);
                      }
                    }
                  }
                }
              }
              return d;
            });
          }
          return result;
        });

      setExistingData(response);

      const hasSite = formData.indicators && formData.indicators.some((indicator: any) => indicator.site);

      let tempfields: any[] = [
        // {
        //   label: 'Reporter',
        //   key: 'reporter',
        //   type: 'string',
        // },
      ];

      let templateHeader: string[] = [
        // 'Reporter',
      ];

      tempfields.push({
        label: 'Period Date',
        key: 'periodDate',
        type: 'string',
      });
      templateHeader.push('Period Date');

      if (hasSite) {
        tempfields.push({
          label: 'Site',
          key: 'site',
          type: 'string',
        });
        templateHeader.push('Site');
      }

      tempfields.push({
        label: 'Indicator',
        key: 'indicator',
        type: 'string',
      });
      templateHeader.push('Indicator');

      const allDimensionNames: string[] = [];
      const dimensionNameToIndex: { [key: string]: number } = {};

      if (formData.indicators && formData.indicators.length > 0) {
        formData.indicators.forEach((formIndicator: any) => {
          if (formIndicator.indicator.dimensions) {
            formIndicator.indicator.dimensions.forEach((dimension: any) => {
              if (!allDimensionNames.includes(dimension.name)) {
                allDimensionNames.push(dimension.name);
                dimensionNameToIndex[dimension.name] = dimension.index;
              }
            });
          }
        });
      }

      allDimensionNames.sort((a, b) => dimensionNameToIndex[a] - dimensionNameToIndex[b]);

      allDimensionNames.forEach(dimensionName => {
        tempfields.push({
          label: dimensionName,
          key: `Dimension${dimensionName}`,
          type: 'string',
        });
        templateHeader.push(dimensionName);
      });

      tempfields.push({
        label: 'Value',
        key: 'period',
        type: 'string',
      });

      tempfields.push({
        label: 'Annotation',
        key: 'annotation',
        type: 'string',
      });

      tempfields.push({
        label: 'Progress Status',
        key: 'progressStatus',
        type: 'string',
      });

      setFields(tempfields);

      templateHeader.push('Value');
      templateHeader.push('Annotation');
      templateHeader.push('Progress Status');
      let templateRows: string[][] = [];

      const reporters =
        formData.formReporters.length > 0
          ? formData.formReporters
          : [
              {
                id: '',
                organisation: {
                  id: '',
                  funder: null,
                  serviceProvider: null,
                  institution: null,
                },
              },
            ];

      reporters.forEach((reporter: any) => {
        formData.indicators.forEach((formIndicator: any) => {
          let templateRow: string[] = [];
          let templateData: any = {};

          formIndicator.indicator.dimensions.sort((a: any, b: any) => a.index - b.index);

          formIndicator.dimensions.forEach((formIndicatorDimension: any, formIndicatorDimensionIndex: any) => {
            const dimension = formIndicator.indicator.dimensions[formIndicatorDimensionIndex];
            if (!dimension) {
              return;
            }
            const key = `${dimension.name}`;
            templateData[key] = formIndicatorDimension;
          });

          templateRow = [];

          templateRow.push('');

          if (hasSite) {
            templateRow.push(formIndicator.site ? formIndicator.site.name : '');
          }

          templateRow.push(`${formIndicator.indicator.name}`);

          for (const dimensionName of allDimensionNames) {
            templateRow.push(templateData[dimensionName] || '');
          }

          // Find existing value for this specific indicator combination
          let existingValue = '';
          let existingAnnotation = '';
          let existingProgressStatus = '';
          if (response && response.length > 0) {
            const existingDataItem = response.find((item: any) => {
              // Match indicator
              if (item.indicatorId !== formIndicator.indicator.id) return false;

              // Match site
              if (formIndicator.site) {
                if (!item.site || item.site.id !== formIndicator.site.id) return false;
              } else {
                if (item.site) return false;
              }

              // Match reporter
              if (reporter.organisation.id === '') {
                if (item.dimensionReporter !== null) return false;
              } else {
                if (!item.dimensionReporter || item.dimensionReporter.id !== reporter.organisation.id) return false;
              }

              // Match dimensions using getDimensionName for robustness
              if (item.dimensions.length !== formIndicator.dimensions.length) return false;

              // Check if all formIndicator dimensions are present in item dimensions
              for (let i = 0; i < formIndicator.dimensions.length; i++) {
                const formDimensionValue = formIndicator.dimensions[i];
                const formDimensionName = formIndicator.indicator.dimensions[i]?.name;

                // Find if this dimension value exists in the item dimensions with the same dimension name
                const foundInItem = item.dimensions.some((itemDimValue: string) => {
                  const itemDimensionName = getDimensionName(formData, itemDimValue);
                  return itemDimValue === formDimensionValue && itemDimensionName === formDimensionName;
                });

                if (!foundInItem) return false;
              }

              return true;
            });

            if (existingDataItem && existingDataItem.value !== null && existingDataItem.value !== undefined) {
              existingValue = existingDataItem.value.toString();
            }

            if (existingDataItem && existingDataItem.annotation !== null && existingDataItem.annotation !== undefined) {
              existingAnnotation = existingDataItem.annotation;
            }

            if (
              existingDataItem &&
              existingDataItem.progressStatus !== null &&
              existingDataItem.progressStatus !== undefined
            ) {
              existingProgressStatus = existingDataItem.progressStatus;
            }
          }

          templateRow.push(existingValue);
          templateRow.push(existingAnnotation);
          templateRow.push(existingProgressStatus);
          templateRows.push(templateRow);
        });
      });

      const row: TemplateType | undefined = [templateHeader, ...templateRows];
      setTemplate(row);
    } else if (formData && formData.indicators && formData.indicators.length !== 0) {
      const hasSite = formData.indicators.some((indicator: any) => indicator.site);

      let tempfields: any[] = [
        // {
        //   label: 'Reporter',
        //   key: 'reporter',
        // },
      ];
      let templateHeader: string[] = [
        // 'Reporter',
      ];

      tempfields.push({
        label: 'Period Date',
        key: 'periodDate',
        type: 'string',
      });
      templateHeader.push('Period Date');

      if (hasSite) {
        tempfields.push({
          label: 'Site',
          key: 'site',
          type: 'string',
        });
        templateHeader.push('Site');
      }

      tempfields.push({
        label: 'Indicator',
        key: 'indicator',
        type: 'string',
      });
      templateHeader.push('Indicator');

      const reporters =
        formData.formReporters.length > 0
          ? formData.formReporters
          : [
              {
                id: '',
                organisation: {
                  id: '',
                  funder: null,
                  serviceProvider: null,
                  institution: null,
                },
              },
            ];

      let templateRows: string[][] = [];

      const allDimensionNames: string[] = [];
      const dimensionNameToIndex: { [key: string]: number } = {};

      formData.indicators.forEach((formIndicator: any) => {
        if (formIndicator.indicator.dimensions) {
          formIndicator.indicator.dimensions.forEach((dimension: any) => {
            if (!allDimensionNames.includes(dimension.name)) {
              allDimensionNames.push(dimension.name);
              dimensionNameToIndex[dimension.name] = dimension.index;
            }
          });
        }
      });

      allDimensionNames.sort((a, b) => dimensionNameToIndex[a] - dimensionNameToIndex[b]);

      allDimensionNames.forEach(dimensionName => {
        tempfields.push({
          label: dimensionName,
          key: `Dimension${dimensionName}`,
          type: 'string',
        });
        templateHeader.push(dimensionName);
      });

      reporters.forEach((reporter: any) => {
        formData.indicators.forEach((formIndicator: any) => {
          let templateRow: string[] = [];
          let templateData: any = {};

          formIndicator.indicator.dimensions.sort((a: any, b: any) => a.index - b.index);

          formIndicator.dimensions.forEach((formIndicatorDimension: any, formIndicatorDimensionIndex: any) => {
            const dimension = formIndicator.indicator.dimensions[formIndicatorDimensionIndex];
            if (!dimension) {
              return;
            }
            const key = `${dimension.name}`;
            templateData[key] = formIndicatorDimension;
          });

          templateRow = [];

          templateRow.push('');

          if (hasSite) {
            templateRow.push(formIndicator.site ? formIndicator.site.name : '');
          }

          templateRow.push(`${formIndicator.indicator.name}`);

          for (const dimensionName of allDimensionNames) {
            templateRow.push(templateData[dimensionName] || '');
          }

          templateRow.push('');
          templateRows.push(templateRow);
        });
      });

      tempfields.push({
        label: 'Value',
        key: 'period',
        type: 'string',
      });

      tempfields.push({
        label: 'Annotation',
        key: 'annotation',
        type: 'string',
      });

      tempfields.push({
        label: 'Progress Status',
        key: 'progressStatus',
        type: 'string',
      });

      setFields(tempfields);
      templateHeader.push('Value');
      templateHeader.push('Annotation');
      templateHeader.push('Progress Status');
      const row: TemplateType | undefined = [templateHeader, ...templateRows];
      setTemplate(row);
    }
  };

  const getDimensionName = (formData: any, dimensionValue: string) => {
    if (formData && formData.indicators && formData.indicators.length !== 0) {
      const indicator = formData.indicators.find((a: any) => a.dimensions.includes(dimensionValue));

      if (indicator && indicator.dimensions.length > 0) {
        const indexOfDimension = indicator.dimensions.indexOf(dimensionValue);
        if (indexOfDimension !== -1) {
          const dimesionName =
            indicator.indicator.dimensions[indexOfDimension] && indicator.indicator.dimensions[indexOfDimension].name;
          return dimesionName;
        }
      }
    }
    return null;
  };

  return (
    <Modal
      open={props.showUploadModal}
      onClose={() => props.setShowUploadModal(!props.showUploadModal)}
      heading="Upload Data"
      bodyOverflowVisible={true}
      width="1000px"
    >
      <Formik<FormType>
        initialValues={INITIAL_VALUES}
        onSubmit={async values => {
          return await client
            .mutate({
              mutation: UPSERT_MONITORINGFORMSUBMISSION,
              variables: {
                input: {
                  ...values.uploadData,
                  view: 'UPLOAD',
                },
              },
              refetchQueries: [
                {
                  query: GET_PROGRAMME_MONITORINGFORM,
                  variables: { input: { monitoringFormId: props.monitoringFormDataForUpload.id } },
                },
                {
                  query: GET_PROGRAMME_MONITORINGFORMDATA,
                  variables: {
                    input: {
                      submissionIds: props.monitoringFormDataForUpload.reporterSubmissions.map((r: any) => r.id),
                    },
                  },
                },
                { query: ALL_MYFORMS, variables: { input: { portal } } },
                {
                  query: GET_MONITORING_FORM_SUBMISSION,
                  variables: {
                    id:
                      props.monitoringFormDataForUpload.reporterSubmissions[0] &&
                      props.monitoringFormDataForUpload.reporterSubmissions[0].id,
                  },
                },
              ],
            })
            .then(() => 'Success!')
            .catch(e => {
              const msg = parseErrorMessage((e && e.message) || e);
              console.error(msg);
              return msg;
            });
        }}
        render={formikProps => (
          <Flex direction="column" size="1" px={3}>
            <Flex direction="column" size="1" flexWrap="nowrap" justify="space-around" minHeight={170}>
              <FieldRow>
                <AlertComponent
                  title={`Instructions`}
                  message={`
                    Please upload a maximum of 1500 records at a time

                    Note that CSV values are sensitive to:
                      - Typographical errors
                      - Text Case
                      - Naming conventions
                  `}
                />
              </FieldRow>
            </Flex>
            {/* flatfile buttons */}
            <Flex direction="row" size="1" justify="space-between">
              {fields.length > 0 && (
                <Field
                  name="uploadData"
                  render={({ field }: FieldProps<FormType>) => {
                    return (
                      <Flatfile
                        csvFileData={template}
                        csvFileName={`MonitoringFormTemplate.csv`}
                        flatFileDataType="MonitoringForm"
                        flatFileFields={fields}
                        flatFileSubmitHandler={async (results: any) => {
                          // check total number of entries
                          if (results.length > 1500) {
                            throw new Error(
                              'Client error - Please only upload a maximum of 1500 stakeholders at a time'
                            );
                          }

                          formikProps.values.uploadData = getUpsertMonitoringFormSubmissionInput(
                            props.monitoringFormDataForUpload,
                            results,
                            existingData,
                            props.tableData
                          );

                          return formikProps.handleSubmit();
                        }}
                        flatFileValidators={flatfileMonitoringFormValidator}
                        close={() => props.setShowUploadModal(!props.showUploadModal)}
                      />
                    );
                  }}
                />
              )}

              {template && (
                <CSVLink filename={`MonitoringFormTemplate.csv`} data={template}>
                  Generate Template
                </CSVLink>
              )}
            </Flex>
          </Flex>
        )}
      />
    </Modal>
  );
};

export default UploadMonitoringForm;
